import { useState, useEffect, useRef } from "react";
import "./ProductList.css";
import Layout from "../../components/layout_v2/Layout";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import ApiService from "../../components/Api_services";
import { Bookmark, Heart, CalendarDays, X, CirclePlay } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MultiPurpose from "../../components/multipurpose_v2/MultiPurpose.js";
import FallbackImg from "../../components/FallbackImg";
import { useSelector } from "react-redux";
import { ACCESS_KEYS } from "../../constants";
import { hasAccess } from "../../utils/Permission";

function getPagination(current, total) {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 4) pages.push("...");
    let start, end;
    if (current <= 4) {
      start = 2;
      end = 5;
    } else if (current >= total - 3) {
      start = total - 4;
      end = total - 1;
    } else {
      start = current - 1;
      end = current + 1;
    }
    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total) pages.push(i);
    }
    if (current < total - 3) pages.push("...");
    pages.push(total);
  }
  return pages;
}

const ProductListv2 = ({
  fetchCounts,
  clearInput,
  setClearInput,
  dropdownValue = "all",
}) => {
  const [products, setProducts] = useState([]);
  const [frames, setFrames] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pagination, setPagination] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [zoomImg, setZoomImg] = useState(null);
  const handleZoomImg = (img) => {
    setZoomImg(img);
  };
  const handleCloseZoom = () => setZoomImg(null);
  const [wishlistStatus, setWishlistStatus] = useState({});
  const [favoriteStatus, setFavoriteStatus] = useState({});
  const isLoggedIn = !!localStorage.getItem("access_token");
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [notice, setNotice] = useState({ show: false, message: "" });
  const requestRef = useRef(0);
  const prevParamsRef = useRef({
    brands: searchParams.get("brands") || "",
    product: searchParams.get("product") || "",
    attributes: searchParams.get("attributes") || "",
    filter_type: searchParams.get("filter_type") || "",
    search: searchParams.get("search") || "",
  });
  const isPackageFilterType = searchParams.get("filter_type") === "package";
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const filterType = params.get("filter_type");

  const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  const [filterMeta, setFilterMeta] = useState({
    brands: [],
    categories: [],
    attributes: [],
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  function formatEtaDate(dateStr) {
    if (!dateStr) return "--";
    return new Date(dateStr).toLocaleString("en-MY", {
      month: "short",
      year: "numeric",
    });
  }

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const sub_role = useSelector((state) => state.auth.sub_role) || [];
  const is_subuser =
    useSelector((state) => state.auth.user?.is_subuser) || false;

  const canView =
    !is_subuser || hasAccess(sub_role, ACCESS_KEYS.HOME || ACCESS_KEYS.ORDER);
  const canOrder = !is_subuser || hasAccess(sub_role, ACCESS_KEYS.ORDER);

  useEffect(() => {
    const params = Object.fromEntries([...searchParams]);
    const page = parseInt(params.page || 1, 10);
    setCurrentPage(page);
    requestRef.current += 1;
    const currentRequest = requestRef.current;
    const apiParams = {
      brand_id: params.brands,
      product_id: params.product,
      attribute_id: params.attributes,
      filter_type: params.filter_type,
      search: params.search,
      page,
      limit: 16,
      discontinued_only: params.discontinued_only,
      stock_filter: params.stock_filter,
      sort: params.sort,
    };

    const apiParams_empty = {
      brand_id: params.brands,
      product_id: "",
      attribute_id: "",
      filter_type: "",
      search: "1",
      page,
      limit: 16,
      discontinued_only: "",
      stock_filter: "",
      sort: "",
    };
    setProducts([]);
    setLoading(true);

    const currentBrands = searchParams.get("brands");
    const currentProduct = searchParams.get("product");
    const currentSearch = searchParams.get("search");

    // Always run ApiService.getInstockProduct(apiParams_empty) first if empty meta condition is met
    const runEmptyApiFirst = async () => {
      if (
        ((currentBrands != "" && currentBrands != null) ||
          (currentProduct != "" && currentProduct != null) ||
          (currentSearch != null && currentSearch != "")) &&
        filterMeta.brands.length === 0 &&
        filterMeta.categories.length === 0 &&
        filterMeta.attributes.length === 0
      ) {
        try {
          const response = await ApiService.getInstockProduct(apiParams_empty);
          if (response && response.status === 200) {
            if (currentRequest !== requestRef.current) return;
            setFilterMeta(response.data.filter_metadata);
          }
        } catch (error) {
          console.error("Error fetching empty params:", error);
        }
      }
    };

    (async () => {
      await runEmptyApiFirst();
      if (currentRequest !== requestRef.current) return;

      try {
        if (apiParams.filter_type === "package") {
          const packageParams = { ...apiParams, limit: 15 };
          const res = await ApiService.getPackageList(packageParams);
          if (currentRequest !== requestRef.current) return;
          if (res && res.status === 200 && res.data) {
            const pkgList = res.data.package_list || [];
            const normalized = pkgList.map((p) => {
              const rep = (p.sub_items && p.sub_items[0]) || {};
              return {
                id: rep.id || p.item_id || p.package_id,
                package_id: p.package_id,
                package_type: p.package_type ?? "",
                item_code: p.package_name || "",
                nick_name: p.package_description || p.package_name || "",
                item_name: p.package_description || "",
                thumb_url: p.photo_url || rep.photo_url || "",
                photo_url: p.photo_url || rep.photo_url || "",
                lowest_price: parseFloat(p.lowest_price || p.final_price || 0),
                total_brand_price: parseFloat(p.total_brand_price || 0),
                brand_price: parseFloat(
                  p.brand_price || p.total_brand_price || 0,
                ),
                rcp: parseFloat(p.rcp || 0),
                representative_item: rep,
                wishlist_id: p.wishlist_id ?? null,
                favorite_id: p.favorite_id ?? null,
                is_bundle: 1,
                promotions: p.promotions || [],
                product: rep.product || "",
                product_type: "package",
                stock_quantity: Number(
                  p.stock_quantity ?? rep.stock_quantity ?? 0,
                ),
                labels: p.labels || [],
              };
            });

            setProducts(normalized);
            if (res.data?.filter_metadata)
              setFilterMeta(res.data.filter_metadata);
            if (res.data?.pagination) {
              setPagination(res.data.pagination);
              setTotalPages(res.data.pagination.total_pages || 1);
            } else {
              setTotalPages(1);
            }
          } else {
            setProducts([]);
            setTotalPages(1);
          }
        } else {
          const response = await ApiService.getInstockProduct(apiParams);
          if (currentRequest !== requestRef.current) return;
          if (response && response.status === 200) {
            const itemList = response.data?.item_list || [];
            // Normalize: some responses may include bundle/package objects inside item_list.
            const normalized = itemList.map((it) => {
              const hasPackageId =
                it &&
                it.package_id != null &&
                String(it.package_id) !== "" &&
                String(it.package_id) !== "0";
              if (it && (String(it.is_bundle) === "1" || hasPackageId)) {
                const b = it;
                const rep = b.representative_item || {};
                const specs = rep.specifications || [];
                const features = rep.features || [];
                const specFields = {};
                for (let i = 0; i < 3; i++) {
                  const s = specs[i] || {};
                  specFields[`spec_${i + 1}`] = s.spec || "";
                  specFields[`spec_sequence_${i + 1}`] = s.spec_sequence || "0";
                  specFields[`spec_desc_${i + 1}`] = s.spec_desc || "";
                  specFields[`spec_desc_sequence_${i + 1}`] =
                    s.spec_desc_sequence || "0";
                  const f = features[i] || {};
                  specFields[`features_${i + 1}`] = f.features || "";
                  specFields[`features_sequence_${i + 1}`] =
                    f.features_sequence || "0";
                  specFields[`features_desc_${i + 1}`] = f.features_desc || "";
                  specFields[`features_desc_sequence_${i + 1}`] =
                    f.features_desc_sequence || "0";
                }

                return {
                  id: rep.id || b.item_id || b.id,
                  package_id: b.package_id,
                  item_code: b.package_name,
                  nick_name: b.package_description,
                  item_name: b.package_description,
                  thumb_url: b.thumb_url || b.photo_url || rep.thumb_url,
                  photo_url: b.photo_url,
                  lowest_price: parseFloat(
                    b.final_price || b.lowest_price || 0,
                  ),
                  brand_price: parseFloat(
                    b.total_brand_price || b.total_rcp || b.brand_price || 0,
                  ),
                  rcp: parseFloat(b.total_rcp || b.rcp || 0),
                  representative_item: rep,
                  wishlist_id: b.wishlist_id ?? null,
                  favorite_id: b.favorite_id ?? null,
                  is_bundle: 1,
                  promotions: b.promotions || [],
                  product: rep.product || b.product || "",
                  product_type: "bundle",
                  stock_quantity: Number(
                    b.stock_quantity ?? rep.stock_quantity ?? 0,
                  ),
                  ...specFields,
                };
              }

              return it;
            });

            setProducts(normalized);
            if (response.data?.filter_metadata) {
              if ((currentBrands || currentProduct) && currentSearch) {
                setFilterMeta((prevMeta) => ({
                  ...response.data.filter_metadata,
                  ...(currentBrands && { brands: prevMeta.brands }),
                  ...(currentProduct && { categories: prevMeta.categories }),
                }));
              } else {
                setFilterMeta(response.data.filter_metadata);
              }
            }
            if (response.data?.frame) {
              const frameMap = {};
              response.data.frame.forEach((frame) => {
                if (frame.photo_url) frameMap[frame.id] = frame.photo_url;
              });
              setFrames(frameMap);
            }
            if (response.data?.pagination) {
              const paginationData = response.data.pagination;
              setPagination(paginationData);
              setTotalPages(paginationData.total_pages || 1);
            } else {
              setTotalPages(1);
            }
          } else {
            setProducts([]);
            setTotalPages(1);
          }
        }
      } catch (error) {
        setProducts([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
        setIsFirstLoad(false);
      }
    })();
  }, [searchParams, dropdownValue]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", page);
      setSearchParams(newParams);
    }
  };

  useEffect(() => {
    // Track current values for the params
    const curr = {
      brands: searchParams.get("brands") || "",
      product: searchParams.get("product") || "",
      attributes: searchParams.get("attributes") || "",
      filter_type: searchParams.get("filter_type") || "",
      search: searchParams.get("search") || "",
    };

    const prev = prevParamsRef.current || {
      brands: "",
      product: "",
      attributes: "",
      filter_type: "",
      search: "",
    };

    // Reset to page 1 when any of these params was ADDED or its value CHANGED
    const shouldReset = [
      "brands",
      "product",
      "attributes",
      "filter_type",
      "search",
    ].some((k) => {
      // treat empty string and null as empty
      const p = prev[k] || "";
      const c = curr[k] || "";
      // reset when changed (including added or removed)
      return p !== c;
    });

    if (shouldReset) {
      if (searchParams.get("page") !== "1") {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", "1");
        setSearchParams(newParams);
      }
    }
    // 当收到上层 clearInput 指示（不是 openDropdowns）时，清除 URL 中的 search 参数
    // 并将 page 设为 1
    if (clearInput && clearInput !== "openDropdowns") {
      const newParams = new URLSearchParams(searchParams);
      if (newParams.has("search")) {
        newParams.delete("search");
        newParams.set("page", "1");
        setSearchParams(newParams);
      }
      if (typeof setClearInput === "function") setClearInput(false);
    }
    // update previous snapshot
    prevParamsRef.current = curr;
  }, [
    dropdownValue,
    searchParams.get("brands"),
    searchParams.get("product"),
    searchParams.get("attributes"),
    searchParams.get("filter_type"),
    searchParams.get("search"),
    clearInput,
  ]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const formatProductCategory = (item) => {
    const arr = [];

    // spec value
    for (let i = 1; i <= 3; i++) {
      const value = item[`spec_${i}`];
      const sequence = Number(item[`spec_sequence_${i}`]);
      if (value && [1, 2, 3].includes(sequence)) {
        arr.push({ value, sequence });
      }
    }
    // spec desc
    for (let i = 1; i <= 3; i++) {
      const desc = item[`spec_desc_${i}`];
      const desc_sequence = Number(item[`spec_desc_sequence_${i}`]);
      if (desc && [1, 2, 3].includes(desc_sequence)) {
        arr.push({ value: desc, sequence: desc_sequence });
      }
    }
    // feature value
    for (let i = 1; i <= 3; i++) {
      const value = item[`features_${i}`];
      const sequence = Number(item[`features_sequence_${i}`]);
      if (value && [1, 2, 3].includes(sequence)) {
        arr.push({ value, sequence });
      }
    }
    // feature desc
    for (let i = 1; i <= 3; i++) {
      const desc = item[`features_desc_${i}`];
      const desc_sequence = Number(item[`features_desc_sequence_${i}`]);
      if (desc && [1, 2, 3].includes(desc_sequence)) {
        arr.push({ value: desc, sequence: desc_sequence });
      }
    }

    // 按 sequence 排序
    arr.sort((a, b) => a.sequence - b.sequence);

    // max 3 items
    return arr.map((x) => x.value).slice(0, 3);
  };

  // function getPromotionTags(promotions = []) {
  //   const tags = [];
  //   promotions.forEach((promo) => {
  //     switch (parseInt(promo.promotion_type)) {
  //       case 2: // Free Gift
  //         tags.push({
  //           label: "Free Gift",
  //           icon: <FontAwesomeIcon icon={faGift} />,
  //         });
  //         break;
  //       case 3: // 普通折扣，无额外标签
  //         tags.push({
  //           label: "Item Discount",
  //           icon: (
  //             <FallbackImg
  //               src={BadgePercent}
  //               alt="badgepercent"
  //               width="13"
  //               height="13"
  //               style={{
  //                 filter:
  //                   "invert(23%) sepia(13%) saturate(1162%) hue-rotate(54deg) brightness(93%) contrast(92%)",
  //               }}
  //             />
  //           ),
  //         });
  //         break;
  //       case 4: // Quantity Offer (all offers as one tag)
  //         tags.push({
  //           label: "Quantity Offer",
  //           icon: <FontAwesomeIcon icon={faGift} />,
  //         });
  //         break;
  //       case 5: // PWP
  //         tags.push({
  //           label: "PWP",
  //           icon: (
  //             <FallbackImg
  //               src={BadgePercent}
  //               alt="badgepercent"
  //               width="13"
  //               height="13"
  //               style={{
  //                 filter:
  //                   "invert(23%) sepia(13%) saturate(1162%) hue-rotate(54deg) brightness(93%) contrast(92%)",
  //               }}
  //             />
  //           ),
  //         });
  //         break;
  //       case 6: // Display Discount
  //         tags.push({
  //           label: "Display Discount",
  //           icon: (
  //             <FallbackImg
  //               src={BadgePercent}
  //               alt="badgepercent"
  //               width="13"
  //               height="13"
  //               style={{
  //                 filter:
  //                   "invert(23%) sepia(13%) saturate(1162%) hue-rotate(54deg) brightness(93%) contrast(92%)",
  //               }}
  //             />
  //           ),
  //         });
  //         break;
  //       default:
  //         break;
  //     }
  //   });
  //   return tags;
  // }

  const getProductPricing = (item) => {
    const lowestPrice = parseFloat(item.lowest_price || 0);
    const highestPrice = parseFloat(item.highest_price || 0);
    const brandPrice = parseFloat(item.brand_price || 0);

    const isPriceRange =
      !isNaN(lowestPrice) && !isNaN(highestPrice) && highestPrice > lowestPrice;

    if (isPriceRange) {
      return {
        displayPrice: formatCurrency(lowestPrice),
        originalPrice: null,
        discountPercent: 0,
        isPriceRange: true,
      };
    }

    if (lowestPrice > 0) {
      let discountPercent = 0;
      if (brandPrice > 0 && lowestPrice < brandPrice) {
        discountPercent = Math.round(
          ((brandPrice - lowestPrice) / brandPrice) * 100,
        );
      }
      return {
        displayPrice: formatCurrency(lowestPrice),
        originalPrice:
          brandPrice > 0 && lowestPrice < brandPrice ? brandPrice : null,
        discountPercent,
      };
    }

    // 优先查找 type 3 promotion
    const type3Promo = (item.promotions || []).find(
      (promo) => String(promo.promotion_type) === "3",
    );
    if (type3Promo && type3Promo.brand_price) {
      const brandPrice = parseFloat(item.brand_price || 0);
      const promoPrice = parseFloat(type3Promo.brand_price || 0);
      let discountPercent = 0;
      if (promoPrice > 0 && promoPrice < brandPrice) {
        discountPercent = Math.round(
          ((brandPrice - promoPrice) / brandPrice) * 100,
        );
      }
      return {
        displayPrice: formatCurrency(promoPrice),
        originalPrice: brandPrice,
        discountPercent,
      };
    }

    // 其它类型按原有逻辑
    const promotionPrice = parseFloat(item.promotion_price || 0);

    let displayPrice = formatCurrency(brandPrice);
    let originalPrice = null;
    let discountPercent = 0;

    if (promotionPrice > 0 && promotionPrice < brandPrice) {
      displayPrice = formatCurrency(promotionPrice);
      originalPrice = brandPrice;
      discountPercent = Math.round(
        ((brandPrice - promotionPrice) / brandPrice) * 100,
      );
    }

    return {
      displayPrice,
      originalPrice,
      discountPercent,
    };
  };

  // Generate maps for brands, products, and attributes
  const brandMap = Object.fromEntries(
    (filterMeta.brands || []).map((b) => [String(b.brand_id), b.brand_name]),
  );
  const productMap = Object.fromEntries(
    (filterMeta.categories || []).map((p) => [
      p.category_code,
      p.category_name,
    ]),
  );
  const attributeMap = {};
  (filterMeta.attributes || []).forEach((attr) =>
    attr.attribute.forEach((val) => {
      attributeMap[String(val.id)] = val.name;
    }),
  );

  const selectedBrands = searchParams.get("brands")
    ? searchParams.get("brands").split(",")
    : [];
  const selectedProduct = searchParams.get("product");
  const selectedAttributes = searchParams.get("attributes")
    ? searchParams.get("attributes").split(",")
    : [];
  const hasFilter =
    selectedBrands.length > 0 ||
    selectedProduct ||
    selectedAttributes.length > 0;

  function buildStatusMap(data) {
    const map = {};
    (data || []).forEach((item) => {
      // const bundleIdRaw = item.bundle_id ?? item.bundle ?? "";
      // const isBundle =
      //   String(item.is_bundle) === "1" ||
      //   (bundleIdRaw !== "" && String(bundleIdRaw) !== "0");
      // const keyId = isBundle
      //   ? bundleIdRaw !== "" && String(bundleIdRaw) !== "0"
      //     ? String(bundleIdRaw)
      //     : String(item.item_id)
      //   : String(item.item_id);
      const isBundle = String(item.is_bundle) === "1";
      const keyId = String(item.item_id);
      map[`${keyId}_${isBundle ? "1" : "0"}`] = item.id;
    });
    return map;
  }

  // Wishlist functionality
  useEffect(() => {
    ApiService.checkWishlistStatus().then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        setWishlistStatus(buildStatusMap(res.data));
      }
    });
  }, []);

  const handleWishlistToggle = async (item, product_type) => {
    // prevent wishlist for package items
    if (String(item.product_type) === "package") {
      setNotice({
        show: true,
        message: "Package items cannot be added to wishlist.",
      });
      return;
    }
    // derive keyItemId: use representative/package id for bundles if present
    const keyItemId = item.is_bundle
      ? String(item.representative_item?.id ?? item.package_id ?? item.id)
      : String(item.id);
    const key = `${keyItemId}_${item.is_bundle ? "1" : "0"}`;
    const wishlistId = wishlistStatus[key];

    try {
      if (wishlistId) {
        const res = await ApiService.removeFromWishlist({
          wishlist_id: wishlistId,
          pricing_type: product_type,
        });
        if (res.status === 200) {
          setWishlistStatus((prev) => {
            const n = { ...prev };
            delete n[key];
            return n;
          });
          if (typeof fetchCounts === "function") fetchCounts();
        }
      } else {
        // backend requires both item_id and bundle_id
        const bundleId = item.is_bundle
          ? String(item.package_id ?? item.representative_item?.id ?? item.id)
          : "0";
        const res = await ApiService.addToWishlist({
          item_id: String(item.id),
          bundle_id: bundleId,
          pricing_type: product_type,
        });
        if (res.status === 200) {
          const checkRes = await ApiService.checkWishlistStatus();
          if (checkRes.status === 200 && Array.isArray(checkRes.data)) {
            setWishlistStatus(buildStatusMap(checkRes.data));
          }
          if (typeof fetchCounts === "function") fetchCounts();
        } else if (res.message === "Wishlist cannot exceed 20 items.") {
          setNotice({ show: true, message: res.message });
        }
      }
    } catch (err) {
      console.error("Wishlist toggle error:", err);
      setNotice({ show: true, message: "操作失败，请稍后重试" });
    }
  };

  // Favorite functionality
  useEffect(() => {
    ApiService.checkFavoriteStatus().then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        setFavoriteStatus(buildStatusMap(res.data));
      }
    });
  }, []);

  const handleFavoriteToggle = async (item, product_type) => {
    // prevent favorite for package items
    if (String(item.product_type) === "package") {
      setNotice({
        show: true,
        message: "Package items cannot be added to favorite.",
      });
      return;
    }
    // use package_id as primary id for bundles (fall back to representative_item.id then item.id)
    const keyItemId = item.is_bundle
      ? String(item.package_id ?? item.representative_item?.id ?? item.id)
      : String(item.id);
    const favKey = `${keyItemId}_${item.is_bundle ? "1" : "0"}`;
    const favoriteId = favoriteStatus[favKey];

    try {
      if (favoriteId) {
        const res = await ApiService.removeFromFavorite({
          favorite_id: favoriteId,
          pricing_type: product_type,
        });

        if (res.status === 200) {
          setFavoriteStatus((prev) => {
            const newStatus = { ...prev };
            delete newStatus[favKey];
            return newStatus;
          });

          try {
            const checkRes = await ApiService.checkFavoriteStatus();
            if (checkRes.status === 200 && Array.isArray(checkRes.data)) {
              setFavoriteStatus(buildStatusMap(checkRes.data));
            }
          } catch (error) {
            console.error("Failed to refresh favorite status:", error);
          }
        } else {
          console.error("Failed to remove from favorites:", res);
        }
      } else {
        const bundleId = item.is_bundle
          ? String(item.package_id ?? item.representative_item?.id ?? item.id)
          : "0";
        const res = await ApiService.addToFavorite({
          item_id: String(item.id),
          bundle_id: bundleId,
          pricing_type: product_type,
        });

        if (res.status === 200) {
          const checkRes = await ApiService.checkFavoriteStatus();
          if (checkRes.status === 200 && Array.isArray(checkRes.data)) {
            setFavoriteStatus(buildStatusMap(checkRes.data));
          }
        } else {
          console.error("Failed to add to favorites:", res);
          if (res.message) {
            setNotice({
              show: true,
              message: res.message,
            });
          }
        }
      }
    } catch (error) {
      console.error("Error in handleFavoriteToggle:", error);
      setNotice({
        show: true,
        message: "Error in handleFavoriteToggle",
      });
    }
  };

  // Compare functionality
  const [compareIds, setCompareIds] = useState(() => {
    const saved = localStorage.getItem("compareIds");
    return saved ? JSON.parse(saved) : [];
  });

  const deriveCompareKey = (it) => {
    const hasPackageId =
      it?.package_id != null &&
      String(it.package_id) !== "" &&
      String(it.package_id) !== "0";
    // const hasBundleId =
    //   it?.bundle_id != null &&
    //   String(it.bundle_id) !== "" &&
    //   String(it.bundle_id) !== "0";
    // const isBundle =
    //   String(it?.is_bundle) === "1" || hasPackageId || hasBundleId;
    const isBundle = String(it?.is_bundle) === "1" || hasPackageId;
    return isBundle
      ? String(
          it.package_id ?? it.representative_item?.id ?? it.item_id ?? it.id,
        )
      : String(it.item_id ?? it.id);
  };

  // ensure saved compareIds belong to current product group OR allow bundle list
  useEffect(() => {
    const currentProduct = searchParams.get("product");
    const filterType = searchParams.get("filter_type");
    const saved = JSON.parse(localStorage.getItem("compareIds") || "[]");
    const allMatch =
      Array.isArray(saved) && saved.length > 0
        ? saved.every(
            (i) => i.product === currentProduct || filterType === "bundle",
          )
        : true;
    if (!allMatch) {
      setCompareIds([]);
      localStorage.removeItem("compareIds");
    }
  }, [searchParams]);

  // 持久化 compareIds
  useEffect(() => {
    if (compareIds.length === 0) {
      localStorage.removeItem("compareIds");
    } else {
      localStorage.setItem("compareIds", JSON.stringify(compareIds));
    }
  }, [compareIds]);

  const handleCompareToggle = (item) => {
    const filterType = searchParams.get("filter_type");

    // Distinguish between a compare entry object (from footer) and a product item
    const isCompareEntry = Boolean(item && item.type);

    const hasPackageId =
      item?.package_id != null &&
      String(item.package_id) !== "" &&
      String(item.package_id) !== "0";
    // const hasBundleId =
    //   item?.bundle_id != null &&
    //   String(item.bundle_id) !== "" &&
    //   String(item.bundle_id) !== "0";
    const isBundle = isCompareEntry
      ? item.type === "bundle"
      : // : String(item.is_bundle) === "1" || hasPackageId || hasBundleId;
        String(item.is_bundle) === "1" || hasPackageId;

    // If this is a product (not a footer entry) and it's a bundle, prevent adding bundles to compare
    if (!isCompareEntry && isBundle) return;

    const allow = isCompareEntry
      ? true
      : !!selectedProduct || filterType === "bundle";
    if (!allow) return;

    const compareKey = deriveCompareKey(item);
    if (!compareKey) return;

    setCompareIds((prev) => {
      const exists = prev.find(
        (i) =>
          String(i.id) === compareKey &&
          i.type === (isBundle ? "bundle" : "item"),
      );
      if (exists)
        return prev.filter(
          (i) =>
            !(
              String(i.id) === compareKey &&
              i.type === (isBundle ? "bundle" : "item")
            ),
        );
      if (prev.length >= 3) return prev;
      return [
        ...prev,
        {
          id: compareKey,
          type: isBundle ? "bundle" : "item",
          item_id: String(item.item_id ?? item.id ?? ""),
          package_id: isBundle ? compareKey : null,
          photo_url: item.photo_url,
          item_code: item.item_code,
          nick_name: item.nick_name,
          product:
            selectedProduct || (isBundle ? "bundle" : item.product || ""),
        },
      ];
    });
  };

  // function getFrameType(item) {
  //   // New Arrival: launched within last 30 days
  //   const launchDate = new Date(item.launching_date);
  //   const thirtyDaysAgo = new Date();
  //   thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  //   if (launchDate > thirtyDaysAgo) {
  //     return "new_arrival";
  //   }

  //   // Promo: has promotion
  //   if (item.promotions && item.promotions.length > 0) {
  //     return "promo";
  //   }

  //   // Coming soon: url has filter_type=coming_soon
  //   // if (searchParams.get("filter_type") === "coming_soon") {
  //   //   return "coming_soon";
  //   // }
  //   if (
  //     item.step > 5 &&
  //     item.step < 10 &&
  //     item.launching_date == "0000-00-00 00:00:00"
  //   ) {
  //     return "coming_soon";
  //   }

  //   return null; // No frame
  // }

  return (
    <Layout
      brandList={filterMeta.brands || []}
      productGroupList={filterMeta.categories || []}
      attributeList={filterMeta.attributes || []}
      setClearInput={setClearInput}
    >
      <div className="productlist_v2">
        {canView ? (
          <>
            <div className="productlist-content_v2">
              <div className="productlist-main_v2">
                {hasFilter && (
                  <div className="filter-bar_v2">
                    <button
                      className="clear-all-btn_v2"
                      onClick={() => {
                        setLoading(true); // Show loading immediately
                        const newParams = new URLSearchParams();
                        const filterType = searchParams.get("filter_type");
                        if (filterType)
                          newParams.set("filter_type", filterType);
                        newParams.set("page", "1");
                        setSearchParams(newParams);
                        if (typeof setClearInput === "function")
                          setClearInput(true);
                      }}
                    >
                      Clear All
                    </button>
                    {selectedBrands.map((brandId) => (
                      <span
                        className="filter-tag_v2"
                        key={brandId}
                        title={brandMap[brandId]}
                      >
                        <div className="filter-tag-text_v2">
                          {brandMap[brandId]}
                        </div>
                        <button
                          className="filter-tag-close_v2"
                          onClick={() => {
                            const remain = selectedBrands
                              .filter((id) => id !== brandId)
                              .join(",");
                            const newParams = new URLSearchParams(searchParams);
                            if (remain) {
                              newParams.set("brands", remain);
                            } else {
                              newParams.delete("brands");
                            }
                            newParams.set("page", 1);
                            setSearchParams(newParams);
                          }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    {selectedProduct && (
                      <span
                        className="filter-tag_v2"
                        title={productMap[selectedProduct]}
                      >
                        <div className="filter-tag-text_v2">
                          {productMap[selectedProduct]}
                        </div>
                        <button
                          className="filter-tag-close_v2"
                          onClick={() => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.delete("product");
                            newParams.set("page", 1);
                            setSearchParams(newParams);
                          }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}
                    {selectedAttributes.map((attrId) => (
                      <span
                        className="filter-tag_v2"
                        key={attrId}
                        title={attributeMap[attrId]}
                      >
                        <div className="filter-tag-text_v2">
                          {attributeMap[attrId]}
                        </div>
                        <button
                          className="filter-tag-close_v2"
                          onClick={() => {
                            const remain = selectedAttributes
                              .filter((id) => id !== attrId)
                              .join(",");
                            const newParams = new URLSearchParams(searchParams);
                            if (remain) {
                              newParams.set("attributes", remain);
                            } else {
                              newParams.delete("attributes");
                            }
                            newParams.set("page", 1);
                            setSearchParams(newParams);
                          }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div
                className={`pl-product-container_v2 ${isPackageFilterType ? " package" : ""}`}
              >
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    Loading products...
                  </div>
                ) : products.length > 0 ? (
                  products.map((item, index) => {
                    const category = formatProductCategory(item);
                    const pricing = getProductPricing(item);
                    const outOfStock = item.labels?.some(
                      (label) => label.tag === "out_of_stock",
                    );
                    const isDiscontinued =
                      item.status == 2 && outOfStock && item.eta_date === "";
                    const isNotForSale = item.out_of_stock === "1";
                    const isPackage =
                      String(item.product_type) === "package" ||
                      String(item.product_type) === "Package";
                    const hasComingSoon = item.labels?.some(
                      (label) => label.tag === "coming_soon",
                    );

                    const itemLinkParams = new URLSearchParams();
                    const isBundleFlag =
                      String(item.is_bundle) === "1" || item.is_bundle === true;
                    if (isBundleFlag) itemLinkParams.set("isBundle", "1");
                    const pkgId =
                      item.package_id ??
                      item.representative_item?.id ??
                      item.item_id ??
                      item.id;
                    if (pkgId && isBundleFlag)
                      itemLinkParams.set("bundleId", String(pkgId));
                    const isGroupPackage =
                      isPackageFilterType && String(item.package_type) === "1";
                    const linkHref = isGroupPackage
                      ? `/grouppackagedetail/${item.package_id}`
                      : isPackageFilterType
                        ? `/packageproductdetail/${item.package_id}`
                        : `/productdetail/${item.id}${itemLinkParams.toString() ? `?${itemLinkParams.toString()}` : ""}`;
                    return (
                      <div
                        key={`${item.id}-${index}`}
                        className="pl-product_v2"
                      >
                        <div className="pl-product-internal_v2">
                          {/* Heart icon or bookmark icon */}
                          {!isDiscontinued &&
                            !isPackage &&
                            (() => {
                              const isBundle =
                                String(item.is_bundle) === "1" ||
                                item.is_bundle === true;

                              const candidates = isBundle
                                ? [
                                    String(item.package_id ?? ""),
                                    String(item.representative_item?.id ?? ""),
                                    String(item.item_id ?? ""),
                                    String(item.id ?? ""),
                                  ].filter((v) => v !== "" && v !== "0")
                                : [
                                    String(item.item_id ?? item.id ?? ""),
                                  ].filter((v) => v !== "");

                              let foundKey = null;
                              for (const cid of candidates) {
                                const key = `${cid}_${isBundle ? "1" : "0"}`;
                                if (favoriteStatus[key]) {
                                  foundKey = key;
                                  break;
                                }
                              }
                              const isFav = !!foundKey;

                              const wishlistKey = `${isBundle ? String(item.representative_item?.id ?? item.package_id ?? item.id) : String(item.id)}_${isBundle ? "1" : "0"}`;

                              if (outOfStock) {
                                return (
                                  <button
                                    className="pl-bookmark-btn_v2"
                                    title={
                                      canOrder
                                        ? wishlistStatus[wishlistKey]
                                          ? "Remove from wishlist"
                                          : "Add to wishlist"
                                        : "No permission"
                                    }
                                    onClick={(e) => {
                                      if (!canOrder) return;
                                      e.stopPropagation();
                                      e.preventDefault();
                                      if (
                                        !localStorage.getItem("access_token")
                                      ) {
                                        setShowLoginPrompt(true);
                                        return;
                                      }
                                      handleWishlistToggle(
                                        item,
                                        item.product_type,
                                      );
                                    }}
                                    disabled={!canOrder}
                                    style={{
                                      opacity: canOrder ? 1 : 0.5,
                                      cursor: canOrder
                                        ? "pointer"
                                        : "not-allowed",
                                    }}
                                  >
                                    <Bookmark
                                      size={18}
                                      color={
                                        wishlistStatus[wishlistKey]
                                          ? "#f47920"
                                          : "#525252"
                                      }
                                      fill={
                                        wishlistStatus[wishlistKey]
                                          ? "#f47920"
                                          : "none"
                                      }
                                    />
                                  </button>
                                );
                              }

                              return (
                                <button
                                  className="pl-fav-btn_v2"
                                  title={
                                    canOrder
                                      ? isFav
                                        ? "Remove from favorite"
                                        : "Add to favorite"
                                      : "No permission"
                                  }
                                  onClick={(e) => {
                                    if (!canOrder) return;
                                    e.stopPropagation();
                                    e.preventDefault();
                                    if (!localStorage.getItem("access_token")) {
                                      setShowLoginPrompt(true);
                                      return;
                                    }
                                    handleFavoriteToggle(
                                      item,
                                      item.product_type,
                                    );
                                  }}
                                  disabled={!canOrder}
                                  style={{
                                    opacity: canOrder ? 1 : 0.5,
                                    cursor: canOrder
                                      ? "pointer"
                                      : "not-allowed",
                                  }}
                                >
                                  <Heart
                                    key={`favorite-${item.id}-${isFav ? "1" : "0"}`}
                                    size={18}
                                    color={isFav ? "#e63946" : "#525252"}
                                    fill={isFav ? "#e63946" : "none"}
                                  />
                                </button>
                              );
                            })()}

                          {/* Frame tag */}
                          {item.labels?.filter(
                            (label) =>
                              label.tag !== "out_of_stock" &&
                              label.tag !== "discontinued_can_buy",
                          ).length > 0 && (
                            <div className="frametag-container_v2">
                              {item.labels
                                .filter(
                                  (label) =>
                                    label.tag !== "out_of_stock" &&
                                    label.tag !== "discontinued_can_buy",
                                )
                                .sort((a, b) =>
                                  a.tag === "new"
                                    ? -1
                                    : b.tag === "new"
                                      ? 1
                                      : 0,
                                )
                                .map((label, index) => (
                                  <div
                                    className="frametag_v2"
                                    key={index}
                                    style={{ backgroundColor: label.color }}
                                  >
                                    {label.tag
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                                  </div>
                                ))}
                            </div>
                          )}

                          <Link
                            to={linkHref}
                            state={{
                              package_type: item.package_type,
                              fromFilterType: filterType,
                            }}
                          >
                            <div className="product-image-wrapper_v2">
                              <FallbackImg
                                src={item.thumb_url}
                                alt={item.item_code}
                              />
                              {item.item_video && (
                                <div className="video-icon_v2">
                                  <CirclePlay
                                    className="circleplay-icon_v2"
                                    size={16}
                                  />
                                </div>
                              )}
                              {(() => {
                                const OVERLAY_LABELS = [
                                  {
                                    tag: "discontinued",
                                    text: "DISCONTINUED",
                                  },
                                  { tag: "coming_soon", text: "COMING SOON" },
                                  {
                                    tag: "not_for_sell",
                                    text: "NOT FOR SELL",
                                  },
                                  {
                                    tag: "out_of_stock",
                                    text: "OUT OF STOCK",
                                  },
                                ];

                                const matchedLabel = item.labels?.find(
                                  (label) =>
                                    OVERLAY_LABELS.some(
                                      (o) => o.tag === label.tag,
                                    ),
                                );

                                if (!matchedLabel) return null;

                                const overlayText = OVERLAY_LABELS.find(
                                  (o) => o.tag === matchedLabel.tag,
                                )?.text;

                                return (
                                  <div className="pl-overlay_v2">
                                    <div className="pl-overlay-text_v2">
                                      {overlayText}
                                    </div>
                                    {matchedLabel.tag !==
                                      "discontinued_can_buy" &&
                                      matchedLabel.tag !== "discontinued" && (
                                        <div className="pl-eta-pill_v2">
                                          <CalendarDays size={13} />
                                          <span>
                                            {item.eta_date
                                              ? formatDate(item.eta_date)
                                              : "--"}
                                          </span>
                                        </div>
                                      )}
                                  </div>
                                );
                              })()}
                            </div>

                            <div
                              className={`product-info_v2 ${isPackageFilterType ? "" : " standard"}`}
                            >
                              <div className="productname_v2">
                                {item.item_code}
                              </div>
                              <div
                                className="description_v2"
                                title={item.nick_name || item.item_name}
                              >
                                {item.nick_name || item.item_name}
                              </div>
                              {!isPackage && (
                                <ul className="productcategory_v2">
                                  {category.map((sp, i) => (
                                    <li
                                      key={i}
                                      className="productcategory-item_v2"
                                    >
                                      {sp}
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {isPackage ? (
                                <div className="package-price-container_v2">
                                  <span className="package-price-label_v2">
                                    Starting from
                                  </span>
                                  <span className="package-price_v2">
                                    RM{" "}
                                    {formatCurrency(
                                      item.total_brand_price ??
                                        item.brand_price ??
                                        0,
                                    )}
                                    ++
                                  </span>
                                  <span className="package-price-note_v2">
                                    Pricing varies by selection
                                  </span>
                                </div>
                              ) : !isDiscontinued &&
                                !item.labels?.some(
                                  (label) =>
                                    label.tag === "coming_soon" ||
                                    label.tag === "out_of_stock",
                                ) ? (
                                <div className="price-container_v2">
                                  <div className="price_v2">
                                    {parseFloat(pricing.displayPrice) !== 0 ? (
                                      <>
                                        <span className="price-sub_v2">
                                          FROM
                                        </span>{" "}
                                        RM {pricing.displayPrice}
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                </div>
                              ) : (
                                // empty space
                                <div style={{ minHeight: "28px" }}></div>
                              )}

                              {/* Promo tag */}
                              {/* {!isDiscontinued && !outOfStock && (
                                <div className="promo-tag-container">
                                  {(() => {
                                    const tags = getPromotionTags(
                                      item.promotions,
                                    );
                                    const maxShow = 1;
                                    const hiddenTags = tags.slice(maxShow);

                                    return (
                                      <>
                                        {tags
                                          .slice(0, maxShow)
                                          .map((tag, idx) => (
                                            <div
                                              className="promo-tag"
                                              key={idx}
                                            >
                                              {tag.icon}
                                              <span
                                                className="promo-tag-text"
                                                title={tag.label}
                                              >
                                                {tag.label}
                                              </span>
                                            </div>
                                          ))}
                                        {tags.length > maxShow && (
                                          <div className="promo-add-tag">
                                            +{tags.length - maxShow}
                                            <div className="promo-tooltip">
                                              {hiddenTags.map((tag, idx) => (
                                                <div
                                                  className="promo-tag"
                                                  key={idx}
                                                >
                                                  {tag.icon}
                                                  {tag.label}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )} */}

                              {/* Compare checkbox */}
                              {(() => {
                                const filterType =
                                  searchParams.get("filter_type");
                                const isBundle =
                                  String(item.is_bundle) === "1" ||
                                  item.is_bundle === true;
                                const allowCompare =
                                  !!selectedProduct || filterType === "bundle";

                                // Do not render compare controls
                                if (isBundle || isPackage || !allowCompare)
                                  return null;

                                return (
                                  <div className="compare-checkbox-label-container_v2">
                                    <label
                                      className={`compare-checkbox-label_v2${
                                        compareIds.some((i) => i.id === item.id)
                                          ? " compare-active_v2"
                                          : ""
                                      }`}
                                      title="Compare"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        className="compare-checkbox_v2"
                                        checked={compareIds.some(
                                          (i) =>
                                            String(i.id) ===
                                            deriveCompareKey(item),
                                        )}
                                        onChange={() => {
                                          handleCompareToggle(item);
                                        }}
                                      />
                                      <span>Compare</span>
                                    </label>
                                  </div>
                                );
                              })()}
                            </div>
                          </Link>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    No products found.
                  </div>
                )}
              </div>

              {showLoginPrompt && (
                <MultiPurpose
                  title="Login Required"
                  message="You need to login to use the wishlist feature."
                  yesText="Login"
                  noText="Cancel"
                  showYesLink={true}
                  yesLink="/login"
                  onNo={() => setShowLoginPrompt(false)}
                  onClose={() => setShowLoginPrompt(false)}
                />
              )}

              {notice.show && (
                <MultiPurpose
                  title="Notice"
                  message={notice.message}
                  yesText="OK"
                  showYesLink={false}
                  onNo={() => setNotice({ show: false, message: "" })}
                  onYes={() => setNotice({ show: false, message: "" })}
                  onClose={() => setNotice({ show: false, message: "" })}
                  hideNo={true}
                />
              )}

              {zoomImg && (
                <div className="image-modal_v2" onClick={handleCloseZoom}>
                  <button className="modal-close_v2" onClick={handleCloseZoom}>
                    <X size={16} />
                  </button>
                  <FallbackImg
                    src={zoomImg}
                    alt="zoom"
                    className="modal-image_v2"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}

              {totalPages >= 1 && (
                <div
                  className="pagination-modern_v2"
                  style={{ marginTop: "24px" }}
                >
                  <button
                    className="pagination-btn_v2"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <span className="prevnext-text_v2">Prev</span>
                  </button>
                  {getPagination(currentPage, totalPages).map((p, idx) =>
                    p === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="pagination-ellipsis_v2"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={`page-${p}-${idx}`}
                        className={`pagination-btn_v2${
                          currentPage === p ? " pagination-current_v2" : ""
                        }`}
                        onClick={() => handlePageChange(p)}
                        disabled={currentPage === p}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    className="pagination-btn_v2"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <span className="prevnext-text_v2">Next</span>
                  </button>
                </div>
              )}
            </div>

            {/* Compare Product Footer */}
            {compareIds.length > 0 && (
              <div className="compare-footer_v2">
                <div className="compare-footer-main_v2">
                  <div className="compare-footer-left_v2">
                    <div className="compare-title_v2">
                      COMPARE PRODUCTS ({compareIds.length}/3)
                    </div>
                  </div>

                  <div className="compare-footer-center_v2">
                    {compareIds.slice(0, 3).map((compareItem) => (
                      <div
                        className="compare-item-container_v2"
                        key={compareItem.id}
                      >
                        <div className="compare-item_v2">
                          <FallbackImg
                            src={compareItem.photo_url}
                            alt="compare"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                        <div className="compare-item-info_v2">
                          <div className="compare-item-code_v2">
                            {compareItem.item_code}
                          </div>
                          <div className="compare-item-name_v2">
                            {compareItem.item_name || compareItem.nick_name}
                          </div>
                        </div>
                        <button
                          className="remove-btn_v2"
                          onClick={() => handleCompareToggle(compareItem)}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="compare-footer-right_v2">
                    <button
                      className="cancel-compare-btn_v2"
                      onClick={() => setCompareIds([])}
                    >
                      Clear All
                    </button>
                    <Link
                      to={
                        compareIds.length >= 2
                          ? (() => {
                              // if any selected is a bundle, send package_id (backend accepts package_id OR item_id)
                              const hasBundle = compareIds.some(
                                (i) => i.type === "bundle",
                              );
                              if (hasBundle) {
                                const packageIds = compareIds
                                  .filter((i) => i.type === "bundle")
                                  .map((i) => i.id)
                                  .join(",");
                                return `/compareproduct?package_id=${encodeURIComponent(packageIds)}`;
                              } else {
                                const itemIds = compareIds
                                  .map((i) => i.id)
                                  .join(",");
                                return `/compareproduct?item_id=${encodeURIComponent(itemIds)}`;
                              }
                            })()
                          : "#"
                      }
                      style={{
                        pointerEvents: compareIds.length >= 2 ? "auto" : "none",
                      }}
                    >
                      <button
                        className="compare-btn_v2"
                        disabled={compareIds.length < 2}
                        style={{
                          opacity: compareIds.length < 2 ? 0.5 : 1,
                          cursor:
                            compareIds.length < 2 ? "not-allowed" : "pointer",
                        }}
                      >
                        Compare Now
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <span style={{ color: "#b71c1c", fontWeight: 600 }}>
              You do not have permission to view this section.
            </span>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductListv2;
